/**
 * Library Browser Controller
 *
 * Manages the UI for browsing and importing songs from the user's Suno library.
 * Uses the extension bridge to communicate with the Suno API.
 */

import { elements } from './dom-cache.js';
import { libraryBrowserState, playlistState } from './state.js';
import { sunoService } from '../lib/suno/suno-service.js';
import { updatePlaylistUI, showPlaylist } from './playlist.js';
import { loadTrack, startPlayback } from './playback.js';
import { markDurationsLoadedIfReady } from './file-handler.js';
import { escapeHtml, escapeAttr } from '../lib/html-utils.js';

// Debounce timer for search
let searchDebounceTimer = null;
const SEARCH_DEBOUNCE_MS = 300;

// Per-filter debounce timers (separate timers prevent cross-filter cancellation)
let genreDebounceTimer = null;
let themeDebounceTimer = null;
let lengthDebounceTimer = null;
const FILTER_DEBOUNCE_MS = 300;

// Export progress cleanup function
let exportProgressCleanup = null;

// Connection status listener cleanup
let connectionStatusCleanup = null;

// Infinite scroll: single observer + arm/disarm gate + promise lock
let infiniteScrollObserver = null;
let infiniteScrollArmed = true;
let loadMoreInFlight = null;

// Auto-fetch: progressively loads pages when client-side filters yield sparse results
let autoFetchAbortController = null;
let isAutoFetching = false;
const AUTO_FETCH_DELAY_MS = 400;     // delay between auto-fetch API calls
const AUTO_FETCH_MAX_PAGES = 20;     // safety limit (20 pages × 20 songs = 400 songs max)
const MIN_VISIBLE_RESULTS = 8;       // stop auto-fetching when we have this many filtered results

// Track if connection lost UI is showing
let isConnectionLostUIVisible = false;

// Plasma placeholder images for songs with missing/broken thumbnails
const PLACEHOLDER_COUNT = 10;

function getPlaceholderForSong(songId) {
  let hash = 0;
  for (let i = 0; i < songId.length; i++) {
    hash = ((hash << 5) - hash + songId.charCodeAt(i)) | 0;
  }
  const index = ((hash % PLACEHOLDER_COUNT) + PLACEHOLDER_COUNT) % PLACEHOLDER_COUNT;
  return `/placeholders/plasma/plasma-${index}.jpg`;
}

/**
 * Initialize the library browser
 */
export function initLibraryBrowser() {
  setupEventListeners();
  console.log('[LibraryBrowser] Initialized');
}

/**
 * Set up all event listeners
 */
function setupEventListeners() {
  // Search input
  if (elements.librarySearchInput) {
    elements.librarySearchInput.addEventListener('input', handleSearchInput);
    elements.librarySearchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        clearSearch();
      }
    });
  }

  // Search clear button
  if (elements.librarySearchClear) {
    elements.librarySearchClear.addEventListener('click', (e) => {
      e.stopPropagation();
      clearSearch();
    });
  }

  // Filter toggle button
  if (elements.libraryFilterToggle) {
    elements.libraryFilterToggle.addEventListener('click', (e) => {
      e.stopPropagation();
      toggleFilterPanel();
    });
  }

  // Filter option buttons (likedStatus, audioType, generationOrder)
  // Scoped to filter panel (not #libraryPanel) for reparenting support
  const filterOptions = elements.libraryFilterPanel
    ? elements.libraryFilterPanel.querySelectorAll('.library-filter-option')
    : document.querySelectorAll('.library-filter-option');
  filterOptions.forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      handleFilterOptionClick(btn);
    });
  });

  // Genre filter input
  if (elements.libraryFilterGenre) {
    elements.libraryFilterGenre.addEventListener('input', (e) => {
      handleFilterTextInput('genre', e.target.value);
    });
    elements.libraryFilterGenre.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        e.target.value = '';
        handleFilterTextInput('genre', '');
      }
    });
  }

  // Theme filter input
  if (elements.libraryFilterTheme) {
    elements.libraryFilterTheme.addEventListener('input', (e) => {
      handleFilterTextInput('theme', e.target.value);
    });
    elements.libraryFilterTheme.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        e.target.value = '';
        handleFilterTextInput('theme', '');
      }
    });
  }

  // Date range inputs
  if (elements.libraryFilterDateFrom) {
    elements.libraryFilterDateFrom.addEventListener('change', (e) => {
      updateAdvancedFilter('dateFrom', e.target.value || null);
    });
  }
  if (elements.libraryFilterDateTo) {
    elements.libraryFilterDateTo.addEventListener('change', (e) => {
      updateAdvancedFilter('dateTo', e.target.value || null);
    });
  }

  // Length filter input with preemptive validation (Issue #13)
  if (elements.libraryFilterLength) {
    elements.libraryFilterLength.addEventListener('keydown', handleLengthFilterKeydown);
    elements.libraryFilterLength.addEventListener('input', (e) => {
      handleLengthFilterInput(e.target.value);
    });
    elements.libraryFilterLength.addEventListener('blur', handleLengthFilterBlur);
  }

  // Filter badge remove click handlers (delegated)
  if (elements.libraryFilterBadges) {
    elements.libraryFilterBadges.addEventListener('click', (e) => {
      e.stopPropagation();
      const removeBtn = e.target.closest('.library-filter-badge-remove');
      if (removeBtn) {
        const filterKey = removeBtn.dataset.filterKey;
        const removesBoth = removeBtn.dataset.removesBoth === 'true';
        if (removesBoth) {
          removeFilter('dateFrom');
          removeFilter('dateTo');
        } else {
          removeFilter(filterKey);
        }
      }
      const clearAllBtn = e.target.closest('.library-filter-clear-all');
      if (clearAllBtn) {
        clearAllFilters();
      }
    });
  }

  // View toggle buttons — no #libraryPanel scope needed; these are unique
  // class names and listeners survive reparenting into the modal
  const viewBtns = document.querySelectorAll('.library-view-btn');
  viewBtns.forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation(); // Prevent panel from closing
      handleViewChange(btn.dataset.view);
    });
  });

  // Selection actions
  if (elements.libraryClearSelectionBtn) {
    elements.libraryClearSelectionBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      clearSelection();
      hideExportMenu();
    });
  }

  if (elements.librarySelectAllBtn) {
    elements.librarySelectAllBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      selectAll();
      hideExportMenu();
    });
  }

  if (elements.libraryAddSelectedBtn) {
    elements.libraryAddSelectedBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      addSelectedToPlaylist();
    });
  }

  if (elements.libraryAddAllFilteredBtn) {
    elements.libraryAddAllFilteredBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      handlePrimaryBulkAction();
    });
  }

  if (elements.libraryWholeLibraryCheckbox) {
    elements.libraryWholeLibraryCheckbox.addEventListener('change', () => {
      updateAddAllFilteredVisibility();
    });
  }

  // Export dropdown
  if (elements.libraryExportBtn) {
    elements.libraryExportBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      toggleExportMenu();
    });
  }

  // Export options
  const exportOptions = document.querySelectorAll('.library-export-option');
  exportOptions.forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (btn.disabled) return;
      exportSelected(btn.dataset.format);
      hideExportMenu();
    });
  });

  // Close export menu on outside click
  document.addEventListener('click', (e) => {
    if (elements.libraryExportMenu?.style.display !== 'none' &&
        !e.target.closest('.library-export-dropdown')) {
      hideExportMenu();
    }
  });

  // Retry button
  if (elements.libraryRetryBtn) {
    elements.libraryRetryBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      loadLibrary();
    });
  }

  // Load more button
  if (elements.libraryLoadMoreBtn) {
    elements.libraryLoadMoreBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      loadMore();
    });
  }

  // Retry connection button (shown when extension not detected on panel open)
  if (elements.libraryRetryExtensionBtn) {
    elements.libraryRetryExtensionBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      elements.libraryRetryExtensionBtn.disabled = true;
      elements.libraryRetryExtensionBtn.textContent = 'Connecting…';
      onPanelOpen().finally(() => {
        if (elements.libraryRetryExtensionBtn) {
          elements.libraryRetryExtensionBtn.disabled = false;
          elements.libraryRetryExtensionBtn.textContent = 'Retry Connection';
        }
      });
    });
  }

  // Export cancel button
  if (elements.libraryExportCancelBtn) {
    elements.libraryExportCancelBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      cancelExport();
    });
  }

  // Listen for panel open
  document.addEventListener('panelOpened', (e) => {
    if (e.detail.panel === 'library') {
      onPanelOpen();
    }
  });

  // Infinite scroll via IntersectionObserver (set up after first render)

  // Listen for connection status changes
  connectionStatusCleanup = sunoService.onConnectionStatusChange(handleConnectionStatusChange);

  // Also listen via DOM event for redundancy
  document.addEventListener('showlarkConnectionStatus', (e) => {
    handleConnectionStatusChange(e.detail.status, e.detail.details, e.detail.connected);
  });

  // Listen for extension ready (covers initial late-injection + reconnect via handleConnectionStatusChange)
  document.addEventListener('showlarkExtensionReady', () => {
    // If the library panel is open and showing the extension-status error, auto-recover
    if (libraryBrowserState.isOpen && elements.libraryExtensionStatus?.style.display !== 'none') {
      console.log('[LibraryBrowser] Extension ready event — auto-recovering from extension-status error');
      onPanelOpen();
    }
  });

  // Listen for generation complete to refresh library with new songs
  document.addEventListener('showlarkGenerationComplete', handleLibraryRefreshOnGeneration);
}

/**
 * Handle panel open
 */
async function onPanelOpen() {
  libraryBrowserState.isOpen = true;

  // Check connection status first
  const availability = await sunoService.checkAvailability();
  if (!availability.available) {
    // Check if it's a connection issue vs extension not installed
    const connectionStatus = sunoService.getConnectionStatus();
    if (connectionStatus.status === 'context_invalidated') {
      showConnectionLostError(connectionStatus.details);
      return;
    }
    // Otherwise show normal extension status
    showExtensionStatus();
    return;
  }

  // Hide any error states
  hideConnectionLostError();

  // Hide extension status if visible
  if (elements.libraryExtensionStatus) {
    elements.libraryExtensionStatus.style.display = 'none';
  }

  // Load library if not already loaded
  if (libraryBrowserState.songs.length === 0 && !libraryBrowserState.isLoading) {
    await loadLibrary();
  }

  updateAddAllFilteredVisibility();
}

/**
 * Show extension not available message
 */
function showExtensionStatus() {
  if (elements.libraryExtensionStatus) {
    elements.libraryExtensionStatus.style.display = 'block';
  }
  if (elements.libraryContent) {
    elements.libraryContent.style.display = 'none';
  }
}

/**
 * Handle connection status changes from extension
 */
function handleConnectionStatusChange(status, details, connected) {
  console.log('[LibraryBrowser] Connection status changed:', status, details);

  if (status === 'context_invalidated' || !connected) {
    showConnectionLostError(details);
  } else if (status === 'connected' && connected) {
    hideConnectionLostError();

    // Auto-recover from "extension not available" error state (race condition on page refresh:
    // isAvailable() timed out before content script injected, caching extensionAvailable=false)
    if (libraryBrowserState.isOpen && elements.libraryExtensionStatus?.style.display !== 'none') {
      console.log('[LibraryBrowser] Extension reconnected — auto-recovering from extension-status error');
      onPanelOpen();
    }
  }
}

/**
 * Handle generation complete event - refresh library to show new songs
 */
function handleLibraryRefreshOnGeneration(event) {
  const { songCount } = event.detail || {};
  console.log('[LibraryBrowser] Generation complete, refreshing to show', songCount, 'new songs');

  // Skip refresh if currently loading
  if (libraryBrowserState.isLoading) {
    console.log('[LibraryBrowser] Already loading, skipping refresh');
    return;
  }

  // Only refresh if library has been used before
  const hasLoadedBefore = libraryBrowserState.songs.length > 0 ||
                          libraryBrowserState.total > 0 ||
                          libraryBrowserState.isOpen;

  if (!hasLoadedBefore) {
    console.log('[LibraryBrowser] Library not yet opened, skipping refresh');
    return;
  }

  if (isConnectionLostUIVisible) {
    console.log('[LibraryBrowser] Connection lost, skipping refresh');
    return;
  }

  // Refresh based on panel state
  if (libraryBrowserState.isOpen) {
    // Panel is open - refresh immediately
    loadLibrary();
  } else {
    // Panel is closed - clear cache for fresh load on next open
    libraryBrowserState.songs = [];
    libraryBrowserState.nextCursor = null;
    libraryBrowserState.hasMore = false;
    libraryBrowserState.total = 0;
    console.log('[LibraryBrowser] Marked for refresh on next open');
  }
}

/**
 * Show connection lost error UI
 */
function showConnectionLostError(details = {}) {
  if (isConnectionLostUIVisible) return;
  isConnectionLostUIVisible = true;

  // Create or get the connection error element
  let errorElement = document.getElementById('libraryConnectionError');

  if (!errorElement) {
    errorElement = document.createElement('div');
    errorElement.id = 'libraryConnectionError';
    errorElement.className = 'library-connection-error';
    errorElement.innerHTML = `
      <div class="library-connection-error-content">
        <div class="library-connection-error-icon">
          <i data-lucide="wifi-off"></i>
        </div>
        <div class="library-connection-error-title">Extension connection lost</div>
        <div class="library-connection-error-message">
          The connection to the ShowLark extension was interrupted. This usually happens when the extension is updated or reloaded.
        </div>
        <button class="library-connection-error-btn" id="libraryConnectionRefreshBtn">
          <i data-lucide="refresh-cw"></i>
          Refresh Page
        </button>
      </div>
    `;

    // Insert at the top of the library content area
    if (elements.libraryContent) {
      elements.libraryContent.insertBefore(errorElement, elements.libraryContent.firstChild);
    } else {
      // Fallback: find the library panel and insert there
      const libraryPanel = document.getElementById('libraryPanel');
      if (libraryPanel) {
        const panelContent = libraryPanel.querySelector('.panel-content');
        if (panelContent) {
          panelContent.insertBefore(errorElement, panelContent.firstChild);
        }
      }
    }

    // Add click handler for refresh button
    const refreshBtn = errorElement.querySelector('#libraryConnectionRefreshBtn');
    if (refreshBtn) {
      refreshBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        window.location.reload();
      });
    }

    // Initialize lucide icons
    if (window.lucide) {
      window.lucide.createIcons();
    }
  }

  errorElement.style.display = 'flex';

  // Disconnect infinite scroll observer
  destroyInfiniteScroll();

  // Hide normal content
  if (elements.libraryGrid) {
    elements.libraryGrid.style.display = 'none';
  }
  if (elements.libraryList) {
    elements.libraryList.style.display = 'none';
  }
  if (elements.libraryLoading) {
    elements.libraryLoading.style.display = 'none';
  }
  if (elements.libraryEmpty) {
    elements.libraryEmpty.style.display = 'none';
  }
  if (elements.libraryError) {
    elements.libraryError.style.display = 'none';
  }
  if (elements.libraryLoadMore) {
    elements.libraryLoadMore.style.display = 'none';
  }

  // Disable actions
  if (elements.librarySearchInput) {
    elements.librarySearchInput.disabled = true;
  }
  if (elements.libraryFilterToggle) {
    elements.libraryFilterToggle.disabled = true;
  }
}

/**
 * Hide connection lost error UI and restore normal state
 */
function hideConnectionLostError() {
  if (!isConnectionLostUIVisible) return;
  isConnectionLostUIVisible = false;

  const errorElement = document.getElementById('libraryConnectionError');
  if (errorElement) {
    errorElement.style.display = 'none';
  }

  // Re-enable inputs
  if (elements.librarySearchInput) {
    elements.librarySearchInput.disabled = false;
  }
  if (elements.libraryFilterToggle) {
    elements.libraryFilterToggle.disabled = false;
  }

  // Restore content visibility based on current state
  if (libraryBrowserState.songs.length > 0) {
    if (libraryBrowserState.viewMode === 'grid' && elements.libraryGrid) {
      elements.libraryGrid.style.display = 'grid';
    }
    if (libraryBrowserState.viewMode === 'list' && elements.libraryList) {
      elements.libraryList.style.display = 'flex';
    }
  }
}

/**
 * Load the library from Suno
 */
async function loadLibrary() {
  cancelAutoFetch(); // cancel any in-progress auto-fetch before fresh load
  libraryBrowserState.isLoading = true;
  libraryBrowserState.error = null;
  showLoading();

  try {
    const options = {
      cursor: null,
      pageSize: libraryBrowserState.pageSize,
      filter: libraryBrowserState.filter
    };

    // If there's a search query, use server-side search
    if (libraryBrowserState.searchQuery) {
      options.searchText = libraryBrowserState.searchQuery;
    }

    const result = await sunoService.browseLibrary(options);

    libraryBrowserState.songs = result.songs;
    libraryBrowserState.hasMore = result.hasMore;
    libraryBrowserState.total = result.total;
    libraryBrowserState.nextCursor = result.nextCursor;

    hideLoading();
    renderSongs();
    infiniteScrollArmed = true;
    initInfiniteScroll();
  } catch (error) {
    console.error('[LibraryBrowser] Load error:', error);
    libraryBrowserState.error = error.message;
    showError(error.message);
  } finally {
    libraryBrowserState.isLoading = false;
    updateAddAllFilteredVisibility();
  }
}

/**
 * Load more songs (cursor-based pagination)
 */
async function loadMore() {
  if (libraryBrowserState.isLoading || !libraryBrowserState.hasMore) return;

  libraryBrowserState.isLoading = true;

  try {
    const options = {
      cursor: libraryBrowserState.nextCursor,
      pageSize: libraryBrowserState.pageSize,
      filter: libraryBrowserState.filter
    };

    // If there's a search query, use server-side search
    if (libraryBrowserState.searchQuery) {
      options.searchText = libraryBrowserState.searchQuery;
    }

    const result = await sunoService.browseLibrary(options);

    libraryBrowserState.songs = [...libraryBrowserState.songs, ...result.songs];
    libraryBrowserState.hasMore = result.hasMore;
    libraryBrowserState.nextCursor = result.nextCursor;

    renderSongs();

    // Force re-arm after DOM settles. When client-side filters produce sparse
    // results, the sentinel may never leave the viewport (the exit event that
    // normally re-arms). Using rAF ensures the observer has processed the new
    // layout before we re-arm, preventing a synchronous re-trigger.
    requestAnimationFrame(() => { infiniteScrollArmed = true; });
  } catch (error) {
    console.error('[LibraryBrowser] Load more error:', error);
    showNotification('Failed to load more songs', 'error');
    destroyInfiniteScroll();
  } finally {
    libraryBrowserState.isLoading = false;
  }
}

/**
 * Create the infinite scroll observer once. Watches a stable sentinel element
 * positioned after the grid/list. Uses arm/disarm pattern: triggers loadMore
 * only when the sentinel transitions into view (not when already visible).
 * Re-arms when sentinel exits view.
 */
function initInfiniteScroll() {
  if (infiniteScrollObserver) return; // already initialized
  if (!elements.librarySentinel || !elements.libraryContent) return;

  infiniteScrollObserver = new IntersectionObserver((entries) => {
    const entry = entries[0];

    if (!entry.isIntersecting) {
      // Sentinel left view — re-arm for next scroll
      infiniteScrollArmed = true;
      return;
    }

    // Sentinel entered view — check gates
    if (!infiniteScrollArmed) return;
    if (!libraryBrowserState.hasMore) return;
    if (libraryBrowserState.songs.length === 0) return;
    if (isAutoFetching) return; // defer to auto-fetch loop

    infiniteScrollArmed = false;
    loadMoreGuarded();
  }, {
    root: elements.libraryContent.closest('.panel-content') || null,
    rootMargin: '0px 0px 200px 0px'
  });

  infiniteScrollObserver.observe(elements.librarySentinel);
}

/**
 * Promise-lock wrapper for loadMore(). Prevents concurrent fetches
 * even if the observer callback fires while a fetch is in progress.
 */
function loadMoreGuarded() {
  if (isAutoFetching) return Promise.resolve(); // defer to auto-fetch loop
  if (loadMoreInFlight) return loadMoreInFlight;

  loadMoreInFlight = (async () => {
    try {
      await loadMore();
    } finally {
      loadMoreInFlight = null;
    }
  })();

  return loadMoreInFlight;
}

/**
 * Disconnect and destroy the infinite scroll observer.
 * Called on reset and connection loss.
 */
function destroyInfiniteScroll() {
  cancelAutoFetch();
  if (infiniteScrollObserver) {
    infiniteScrollObserver.disconnect();
    infiniteScrollObserver = null;
  }
  infiniteScrollArmed = true;
  loadMoreInFlight = null;
}

/**
 * Destroy and recreate the infinite scroll observer.
 * Called when the library content moves between DOM containers (sidebar <-> modal)
 * because the IntersectionObserver's root becomes stale after a reparent.
 */
export function reinitLibraryInfiniteScroll() {
  destroyInfiniteScroll();
  if (libraryBrowserState.songs.length > 0) {
    initInfiniteScroll();
  }
}

/**
 * Auto-fetch additional pages when client-side filters produce sparse results.
 *
 * The Suno v3 API only supports server-side search (searchText) and liked filter.
 * All other filters (genre, theme, audioType, generationOrder, date, duration) are
 * client-side. When these filters are active, the initial 20 songs may yield very
 * few visible results. This function progressively fetches more pages until either:
 *   - We have MIN_VISIBLE_RESULTS filtered matches
 *   - The server has no more pages
 *   - We hit AUTO_FETCH_MAX_PAGES safety limit
 *   - The fetch is cancelled (filter change, panel close, etc.)
 */
async function autoFetchForFilters() {
  // Only auto-fetch when client-side filters are active
  if (!hasActiveClientSideFilters()) return;
  if (!libraryBrowserState.hasMore) return;
  if (libraryBrowserState.isLoading) return;

  // Check if current filtered results are already sufficient
  const currentFiltered = applyAdvancedFilters(libraryBrowserState.songs);
  if (currentFiltered.length >= MIN_VISIBLE_RESULTS) return;

  // Cancel any previous auto-fetch
  cancelAutoFetch();

  // Set up abort controller for this run
  autoFetchAbortController = new AbortController();
  const signal = autoFetchAbortController.signal;
  isAutoFetching = true;

  let pagesFetched = 0;

  showAutoFetchIndicator();

  try {
    while (
      libraryBrowserState.hasMore &&
      pagesFetched < AUTO_FETCH_MAX_PAGES &&
      !signal.aborted
    ) {
      // Cancellable delay between API calls
      await new Promise((resolve, reject) => {
        const timer = setTimeout(resolve, AUTO_FETCH_DELAY_MS);
        signal.addEventListener('abort', () => {
          clearTimeout(timer);
          reject(new DOMException('Aborted', 'AbortError'));
        }, { once: true });
      });

      if (signal.aborted) break;

      // Build request options (same as loadMore but bypasses isLoading lock)
      const options = {
        cursor: libraryBrowserState.nextCursor,
        pageSize: libraryBrowserState.pageSize,
        filter: libraryBrowserState.filter
      };
      if (libraryBrowserState.searchQuery) {
        options.searchText = libraryBrowserState.searchQuery;
      }

      const result = await sunoService.browseLibrary(options);

      if (signal.aborted) break;

      // Append results to state
      libraryBrowserState.songs = [...libraryBrowserState.songs, ...result.songs];
      libraryBrowserState.hasMore = result.hasMore;
      libraryBrowserState.nextCursor = result.nextCursor;
      pagesFetched++;

      // Re-render without triggering another auto-fetch (use core)
      renderSongsCore();

      // Check if we now have enough filtered results
      const filteredCount = applyAdvancedFilters(libraryBrowserState.songs).length;
      updateAutoFetchIndicator(pagesFetched, filteredCount);

      if (filteredCount >= MIN_VISIBLE_RESULTS) break;
      if (!libraryBrowserState.hasMore) break;
    }
  } catch (error) {
    if (error.name === 'AbortError') {
      console.log('[LibraryBrowser] Auto-fetch cancelled');
      return; // Clean exit — indicator hidden by cancelAutoFetch caller
    }
    console.error('[LibraryBrowser] Auto-fetch error:', error);
    // Graceful degradation: show whatever we have so far
  } finally {
    isAutoFetching = false;
    autoFetchAbortController = null;
    hideAutoFetchIndicator();

    // Force re-arm infinite scroll so user can continue scrolling
    infiniteScrollArmed = true;

    // Final re-render to show load-more button if there are more pages
    renderSongsCore();
  }
}

/**
 * Show the auto-fetch progress indicator in the load-more area.
 */
function showAutoFetchIndicator() {
  if (elements.libraryLoadMore) {
    elements.libraryLoadMore.style.display = 'flex';
  }
  if (elements.libraryLoadMoreBtn) {
    elements.libraryLoadMoreBtn.innerHTML =
      `<span class="library-spinner-sm"></span> Searching for matching songs...`;
    elements.libraryLoadMoreBtn.disabled = true;
  }
}

/**
 * Update the auto-fetch indicator with progress.
 */
function updateAutoFetchIndicator(pagesFetched, filteredCount) {
  if (elements.libraryLoadMoreBtn) {
    const matchText = filteredCount === 1 ? '1 match' : `${filteredCount} matches`;
    elements.libraryLoadMoreBtn.innerHTML =
      `<span class="library-spinner-sm"></span> Searching... ${matchText} found`;
  }
}

/**
 * Hide the auto-fetch indicator and restore normal load-more button.
 */
function hideAutoFetchIndicator() {
  if (elements.libraryLoadMoreBtn) {
    elements.libraryLoadMoreBtn.innerHTML = 'Load More';
    elements.libraryLoadMoreBtn.disabled = false;
  }
}

/**
 * Handle search input
 */
function handleSearchInput(e) {
  const query = e.target.value.trim();

  // Show/hide clear button
  if (elements.librarySearchClear) {
    elements.librarySearchClear.style.display = query ? 'flex' : 'none';
  }

  // Debounce search
  if (searchDebounceTimer) {
    clearTimeout(searchDebounceTimer);
  }

  searchDebounceTimer = setTimeout(() => {
    performSearch(query);
  }, SEARCH_DEBOUNCE_MS);
}

/**
 * Perform search (uses v3 server-side searchText)
 */
async function performSearch(query) {
  cancelAutoFetch(); // cancel any in-progress auto-fetch before new search
  libraryBrowserState.searchQuery = query;
  libraryBrowserState.isSearching = !!query;
  libraryBrowserState.isLoading = true;
  libraryBrowserState.nextCursor = null;
  showLoading();

  try {
    const options = {
      cursor: null,
      pageSize: libraryBrowserState.pageSize,
      filter: libraryBrowserState.filter
    };

    if (query) {
      options.searchText = query;
    }

    const result = await sunoService.browseLibrary(options);

    libraryBrowserState.songs = result.songs;
    libraryBrowserState.hasMore = result.hasMore;
    libraryBrowserState.nextCursor = result.nextCursor;

    hideLoading();
    renderSongs();
    infiniteScrollArmed = true;
    initInfiniteScroll();
    updateAddAllFilteredVisibility();
  } catch (error) {
    console.error('[LibraryBrowser] Search error:', error);
    showError(error.message);
  } finally {
    libraryBrowserState.isLoading = false;
  }
}

/**
 * Clear search
 */
function clearSearch() {
  if (elements.librarySearchInput) {
    elements.librarySearchInput.value = '';
  }
  if (elements.librarySearchClear) {
    elements.librarySearchClear.style.display = 'none';
  }
  libraryBrowserState.searchQuery = '';
  libraryBrowserState.isSearching = false;
  updateAddAllFilteredVisibility();
  loadLibrary();
}

/**
 * Toggle filter panel visibility
 */
function toggleFilterPanel() {
  libraryBrowserState.filterPanelOpen = !libraryBrowserState.filterPanelOpen;

  if (elements.libraryFilterPanel) {
    elements.libraryFilterPanel.style.display = libraryBrowserState.filterPanelOpen ? 'flex' : 'none';
  }
  if (elements.libraryFilterToggle) {
    elements.libraryFilterToggle.classList.toggle('active', libraryBrowserState.filterPanelOpen);
  }
}

/**
 * Handle filter option button click (toggle groups)
 */
function handleFilterOptionClick(btn) {
  const filterType = btn.dataset.filterType;
  const value = btn.dataset.value;

  // Update state
  libraryBrowserState.advancedFilters[filterType] = value;

  // Maintain backward compatibility with basic filter
  if (filterType === 'likedStatus') {
    libraryBrowserState.filter = value === 'liked' ? 'liked' : 'all';
  }

  // Update button group UI
  const group = btn.closest('.library-filter-toggle-group');
  if (group) {
    group.querySelectorAll('.library-filter-option').forEach(b => {
      b.classList.toggle('active', b === btn);
    });
  }

  // Update badges and reload
  updateFilterBadges();
  updateFilterToggleIndicator();
  loadLibrary();
}

/**
 * Handle text input filter change with debouncing.
 * Uses per-filter timers so typing in one filter input doesn't cancel
 * a pending update for a different filter.
 */
function handleFilterTextInput(filterKey, value) {
  // Select the correct per-filter timer
  if (filterKey === 'genre') {
    if (genreDebounceTimer) clearTimeout(genreDebounceTimer);
    genreDebounceTimer = setTimeout(() => {
      libraryBrowserState.advancedFilters[filterKey] = value.trim();
      updateFilterBadges();
      updateFilterToggleIndicator();
      loadLibrary();
    }, FILTER_DEBOUNCE_MS);
  } else if (filterKey === 'theme') {
    if (themeDebounceTimer) clearTimeout(themeDebounceTimer);
    themeDebounceTimer = setTimeout(() => {
      libraryBrowserState.advancedFilters[filterKey] = value.trim();
      updateFilterBadges();
      updateFilterToggleIndicator();
      loadLibrary();
    }, FILTER_DEBOUNCE_MS);
  }
}

// --------------------------------
// Length Filter (Issue #13)
// --------------------------------

/**
 * Parse length input string to seconds.
 * Supports:
 * - Pure seconds: "90", "120"
 * - Minutes and seconds with 'm': "1m30", "2m", "1m05"
 *
 * Returns { seconds: number | null, error: boolean }
 */
function parseLengthToSeconds(value) {
  if (!value || !value.trim()) {
    return { seconds: null, error: false };
  }

  const trimmed = value.trim().toLowerCase();

  // Format: XmYY (e.g., "1m30", "2m", "10m05")
  const mMatch = trimmed.match(/^(\d{1,3})m(\d{0,2})$/);
  if (mMatch) {
    const minutes = parseInt(mMatch[1], 10);
    const seconds = mMatch[2] ? parseInt(mMatch[2], 10) : 0;

    // Seconds part must be 0-59
    if (seconds > 59) {
      return { seconds: null, error: true };
    }

    const total = minutes * 60 + seconds;
    // 0 total = no filter
    return { seconds: total === 0 ? null : total, error: false };
  }

  // Format: pure seconds (e.g., "90", "120")
  if (/^\d{1,5}$/.test(trimmed)) {
    const seconds = parseInt(trimmed, 10);
    // 0 = no filter
    return { seconds: seconds === 0 ? null : seconds, error: false };
  }

  return { seconds: null, error: true };
}

/**
 * Format seconds to display string for badges (e.g., 90 -> "1m30")
 */
function formatLengthDisplay(seconds) {
  if (seconds == null || seconds <= 0) return '';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins > 0) {
    return secs > 0 ? `${mins}m${secs.toString().padStart(2, '0')}` : `${mins}m`;
  }
  return `${seconds}s`;
}

/**
 * Handle length filter keydown - preemptive validation.
 * Only allows digits, 'm', and navigation/editing keys.
 */
function handleLengthFilterKeydown(e) {
  const key = e.key;
  const value = e.target.value;

  // Allow navigation and editing keys
  const allowedKeys = [
    'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
    'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
    'Home', 'End'
  ];
  if (allowedKeys.includes(key)) {
    // Handle Escape to clear
    if (key === 'Escape') {
      e.target.value = '';
      handleLengthFilterInput('');
    }
    return;
  }

  // Allow Ctrl/Cmd shortcuts (copy, paste, select all)
  if (e.ctrlKey || e.metaKey) {
    return;
  }

  // Only allow digits and 'm'
  if (!/^[0-9m]$/.test(key)) {
    e.preventDefault();
    return;
  }

  // 'm' rules: only one allowed, must come after at least one digit
  if (key === 'm') {
    if (value.includes('m') || value.length === 0 || !/\d/.test(value)) {
      e.preventDefault();
      return;
    }
  }

  // Digits after 'm': only allow up to 2 digits
  if (/\d/.test(key) && value.includes('m')) {
    const afterM = value.split('m')[1] || '';
    if (afterM.length >= 2) {
      e.preventDefault();
      return;
    }
  }
}

/**
 * Handle length filter input change with debouncing.
 * Uses its own timer so it doesn't interfere with genre/theme debounce timers.
 */
function handleLengthFilterInput(value) {
  if (lengthDebounceTimer) {
    clearTimeout(lengthDebounceTimer);
  }

  lengthDebounceTimer = setTimeout(() => {
    const parsed = parseLengthToSeconds(value);
    // Only update if valid (silently ignore invalid input until blur)
    if (!parsed.error) {
      libraryBrowserState.advancedFilters.minLength = parsed.seconds;
      updateFilterBadges();
      updateFilterToggleIndicator();
      loadLibrary();
    }
  }, FILTER_DEBOUNCE_MS);
}

/**
 * Handle length filter blur - normalize display and clear invalid input
 */
function handleLengthFilterBlur(e) {
  const value = e.target.value;
  const parsed = parseLengthToSeconds(value);

  if (parsed.error) {
    // Clear invalid input on blur
    e.target.value = '';
    libraryBrowserState.advancedFilters.minLength = null;
    updateFilterBadges();
    updateFilterToggleIndicator();
    loadLibrary();
  } else if (value && parsed.seconds != null) {
    // Normalize display format (e.g., "90" -> "1m30")
    e.target.value = formatLengthDisplay(parsed.seconds);
  }
}

/**
 * Update advanced filter and reload
 */
function updateAdvancedFilter(key, value) {
  libraryBrowserState.advancedFilters[key] = value;
  updateFilterBadges();
  updateFilterToggleIndicator();
  loadLibrary();
}

/**
 * Remove a specific filter
 */
function removeFilter(filterKey) {
  const defaults = {
    genre: '',
    dateFrom: null,
    dateTo: null,
    audioType: 'all',
    theme: '',
    likedStatus: 'all',
    generationOrder: 'all',
    minLength: null
  };

  libraryBrowserState.advancedFilters[filterKey] = defaults[filterKey];

  // Maintain backward compatibility
  if (filterKey === 'likedStatus') {
    libraryBrowserState.filter = 'all';
  }

  // Update corresponding UI element
  switch (filterKey) {
    case 'genre':
      if (elements.libraryFilterGenre) elements.libraryFilterGenre.value = '';
      break;
    case 'theme':
      if (elements.libraryFilterTheme) elements.libraryFilterTheme.value = '';
      break;
    case 'dateFrom':
      if (elements.libraryFilterDateFrom) elements.libraryFilterDateFrom.value = '';
      break;
    case 'dateTo':
      if (elements.libraryFilterDateTo) elements.libraryFilterDateTo.value = '';
      break;
    case 'minLength':
      if (elements.libraryFilterLength) elements.libraryFilterLength.value = '';
      break;
    case 'audioType':
    case 'likedStatus':
    case 'generationOrder':
      // Reset toggle group (scoped to filter panel, not #libraryPanel,
      // because content may be reparented into the Suno Manager modal)
      if (elements.libraryFilterPanel) {
        elements.libraryFilterPanel.querySelectorAll(`[data-filter-type="${filterKey}"]`).forEach(btn => {
          btn.classList.toggle('active', btn.dataset.value === 'all');
        });
      }
      break;
  }

  updateFilterBadges();
  updateFilterToggleIndicator();
  loadLibrary();
}

/**
 * Clear all filters
 */
function clearAllFilters() {
  // Reset all advanced filters
  libraryBrowserState.advancedFilters = {
    genre: '',
    dateFrom: null,
    dateTo: null,
    audioType: 'all',
    theme: '',
    likedStatus: 'all',
    generationOrder: 'all',
    minLength: null
  };
  libraryBrowserState.filter = 'all';

  // Reset UI inputs
  if (elements.libraryFilterGenre) elements.libraryFilterGenre.value = '';
  if (elements.libraryFilterTheme) elements.libraryFilterTheme.value = '';
  if (elements.libraryFilterDateFrom) elements.libraryFilterDateFrom.value = '';
  if (elements.libraryFilterDateTo) elements.libraryFilterDateTo.value = '';
  if (elements.libraryFilterLength) elements.libraryFilterLength.value = '';

  // Reset toggle groups to 'all' (scoped to filter panel for reparenting support)
  if (elements.libraryFilterPanel) {
    elements.libraryFilterPanel.querySelectorAll('.library-filter-toggle-group').forEach(group => {
      group.querySelectorAll('.library-filter-option').forEach(btn => {
        btn.classList.toggle('active', btn.dataset.value === 'all');
      });
    });
  }

  updateFilterBadges();
  updateFilterToggleIndicator();
  loadLibrary();
}

/**
 * Check if any filters are active
 */
function hasActiveFilters() {
  const f = libraryBrowserState.advancedFilters;
  return f.genre !== '' ||
         f.theme !== '' ||
         f.dateFrom !== null ||
         f.dateTo !== null ||
         f.audioType !== 'all' ||
         f.likedStatus !== 'all' ||
         f.generationOrder !== 'all' ||
         f.minLength !== null;
}

/**
 * Check if any client-side-only filters are active.
 * Excludes likedStatus (server-side via `liked: 'True'` param).
 * Used to decide whether auto-fetch is needed.
 */
function hasActiveClientSideFilters() {
  const f = libraryBrowserState.advancedFilters;
  return f.genre !== '' ||
         f.theme !== '' ||
         f.dateFrom !== null ||
         f.dateTo !== null ||
         f.audioType !== 'all' ||
         f.generationOrder !== 'all' ||
         f.minLength !== null;
}

/**
 * Cancel any in-progress auto-fetch operation.
 * Safe to call even if no auto-fetch is running.
 */
function cancelAutoFetch() {
  if (autoFetchAbortController) {
    autoFetchAbortController.abort();
    autoFetchAbortController = null;
  }
  isAutoFetching = false;
  hideAutoFetchIndicator();
}

/**
 * Update filter toggle button indicator
 */
function updateFilterToggleIndicator() {
  if (elements.libraryFilterToggle) {
    elements.libraryFilterToggle.classList.toggle('has-filters', hasActiveFilters());
  }
}

/**
 * Update filter badges display
 */
function updateFilterBadges() {
  if (!elements.libraryActiveFilters || !elements.libraryFilterBadges) return;

  const filters = libraryBrowserState.advancedFilters;
  const badges = [];

  // Build badge data
  if (filters.likedStatus === 'liked') {
    badges.push({ key: 'likedStatus', label: 'Liked', icon: 'heart' });
  } else if (filters.likedStatus === 'not-liked') {
    badges.push({ key: 'likedStatus', label: 'Not Liked', icon: 'heart-off' });
  }

  if (filters.audioType === 'vocal') {
    badges.push({ key: 'audioType', label: 'Vocal', icon: 'mic' });
  } else if (filters.audioType === 'instrumental') {
    badges.push({ key: 'audioType', label: 'Instrumental', icon: 'music' });
  }

  if (filters.generationOrder === 'first') {
    badges.push({ key: 'generationOrder', label: '1st Version' });
  } else if (filters.generationOrder === 'second') {
    badges.push({ key: 'generationOrder', label: '2nd Version' });
  }

  if (filters.genre) {
    badges.push({ key: 'genre', label: `Genre: ${truncateText(filters.genre, 12)}` });
  }

  if (filters.theme) {
    badges.push({ key: 'theme', label: `Theme: ${truncateText(filters.theme, 12)}` });
  }

  if (filters.dateFrom || filters.dateTo) {
    let dateLabel = 'Date: ';
    if (filters.dateFrom && filters.dateTo) {
      dateLabel += `${formatDateShort(filters.dateFrom)} - ${formatDateShort(filters.dateTo)}`;
    } else if (filters.dateFrom) {
      dateLabel += `from ${formatDateShort(filters.dateFrom)}`;
    } else {
      dateLabel += `until ${formatDateShort(filters.dateTo)}`;
    }
    badges.push({ key: 'dateFrom', label: dateLabel, removesBoth: true, icon: 'calendar' });
  }

  if (filters.minLength != null) {
    badges.push({ key: 'minLength', label: `Min: ${formatLengthDisplay(filters.minLength)}`, icon: 'timer' });
  }

  // Update "Add All Filtered" button visibility
  updateAddAllFilteredVisibility();

  // Render badges
  if (badges.length === 0) {
    elements.libraryActiveFilters.style.display = 'none';
    return;
  }

  elements.libraryActiveFilters.style.display = 'flex';
  let badgeHtml = badges.map(badge => `
    <span class="library-filter-badge">
      ${badge.icon ? `<i data-lucide="${badge.icon}"></i>` : ''}
      <span class="library-filter-badge-label">${escapeHtml(badge.label)}</span>
      <button class="library-filter-badge-remove" data-filter-key="${badge.key}" ${badge.removesBoth ? 'data-removes-both="true"' : ''} title="Remove filter">
        <i data-lucide="x"></i>
      </button>
    </span>
  `).join('');

  // Add clear all button if multiple filters (icon only)
  if (badges.length > 1) {
    badgeHtml += `
      <button class="library-filter-clear-all" title="Clear all filters">
        <i data-lucide="x"></i>
      </button>
    `;
  }

  elements.libraryFilterBadges.innerHTML = badgeHtml;

  // Re-initialize Lucide icons
  if (window.lucide) {
    window.lucide.createIcons();
  }
}

/**
 * Truncate text to max length
 */
function truncateText(text, maxLength) {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}

/**
 * Format date for badge display
 */
function formatDateShort(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Apply advanced filters to songs (client-side filtering)
 * Delegates to applyAdvancedFiltersWithSnapshot using the current filter state.
 */
function applyAdvancedFilters(songs) {
  return applyAdvancedFiltersWithSnapshot(songs, libraryBrowserState.advancedFilters);
}

/**
 * Apply advanced filters using a specific filter object.
 * This is the canonical filter implementation — applyAdvancedFilters() delegates here
 * with the current libraryBrowserState.advancedFilters. The snapshot variant is also
 * used directly by addAllFilteredToPlaylist() to prevent filter drift during async fetches.
 */
function applyAdvancedFiltersWithSnapshot(songs, f) {
  return songs.filter(song => {
    if (f.likedStatus === 'liked' && !song.isLiked) return false;
    if (f.likedStatus === 'not-liked' && song.isLiked) return false;
    if (f.audioType === 'instrumental' && song.hasLyrics) return false;
    if (f.audioType === 'vocal' && !song.hasLyrics) return false;
    if (f.genre) {
      const genreLower = f.genre.toLowerCase();
      const tags = (song.tags || '').toLowerCase();
      const prompt = (song.prompt || '').toLowerCase();
      if (!tags.includes(genreLower) && !prompt.includes(genreLower)) return false;
    }
    if (f.theme) {
      const themeLower = f.theme.toLowerCase();
      const prompt = (song.prompt || '').toLowerCase();
      const title = (song.title || '').toLowerCase();
      if (!prompt.includes(themeLower) && !title.includes(themeLower)) return false;
    }
    if (f.dateFrom || f.dateTo) {
      const songDate = song.createdAt ? new Date(song.createdAt) : null;
      if (!songDate) return false;
      if (f.dateFrom) {
        const fromDate = new Date(f.dateFrom);
        fromDate.setHours(0, 0, 0, 0);
        if (songDate < fromDate) return false;
      }
      if (f.dateTo) {
        const toDate = new Date(f.dateTo);
        toDate.setHours(23, 59, 59, 999);
        if (songDate > toDate) return false;
      }
    }
    if (f.generationOrder !== 'all' && song.generationOrder) {
      if (f.generationOrder === 'first' && song.generationOrder !== 1) return false;
      if (f.generationOrder === 'second' && song.generationOrder !== 2) return false;
    }
    // Minimum length filter (Issue #13)
    if (f.minLength != null) {
      const songDuration = typeof song.duration === 'number' ? song.duration : 0;
      if (songDuration < f.minLength) return false;
    }
    return true;
  });
}

/**
 * Handle view change
 */
function handleViewChange(view) {
  libraryBrowserState.viewMode = view;

  // Update button states
  const viewBtns = document.querySelectorAll('.library-view-btn');
  viewBtns.forEach(btn => {
    btn.classList.toggle('active', btn.dataset.view === view);
  });

  // Update view visibility
  if (elements.libraryGrid) {
    elements.libraryGrid.style.display = view === 'grid' ? 'grid' : 'none';
  }
  if (elements.libraryList) {
    elements.libraryList.style.display = view === 'list' ? 'flex' : 'none';
  }

  // Re-render if songs are loaded
  if (libraryBrowserState.songs.length > 0) {
    renderSongs();
  }
}

/**
 * Render songs based on current view mode.
 * Wrapper that renders then triggers auto-fetch for sparse filter results.
 */
function renderSongs() {
  renderSongsCore();
  // Trigger auto-fetch on next microtask to avoid synchronous recursion.
  // Skip if already auto-fetching to prevent restarts on re-renders (e.g., song clicks).
  if (!isAutoFetching) {
    queueMicrotask(() => autoFetchForFilters());
  }
}

/**
 * Core render logic. Called directly by auto-fetch loop to avoid
 * re-triggering autoFetchForFilters() recursively.
 */
function renderSongsCore() {
  // Apply advanced filters (client-side)
  const songs = applyAdvancedFilters(libraryBrowserState.songs);

  if (songs.length === 0) {
    showEmpty();
    return;
  }

  hideEmpty();
  hideError();

  if (libraryBrowserState.viewMode === 'grid') {
    renderGridView(songs);
  } else {
    renderListView(songs);
  }

  // Update load more button (hidden during auto-fetch — indicator replaces it)
  if (elements.libraryLoadMore) {
    elements.libraryLoadMore.style.display =
      (libraryBrowserState.hasMore && !isAutoFetching) ? 'flex' : 'none';
  }
}

/**
 * Render grid view
 */
function renderGridView(songs) {
  if (!elements.libraryGrid) return;

  elements.libraryGrid.innerHTML = songs.map(song => `
    <div class="library-song-card ${libraryBrowserState.selectedIds.has(song.id) ? 'selected' : ''}"
         data-song-id="${song.id}">
      <div class="library-song-card-image">
        ${song.imageUrl
          ? `<img src="${escapeAttr(song.imageUrl)}" alt="${escapeAttr(song.title)}" loading="lazy">`
          : `<img src="${getPlaceholderForSong(song.id)}" alt="" loading="lazy">`
        }
        <div class="library-song-card-checkbox">
          ${libraryBrowserState.selectedIds.has(song.id) ? '<i data-lucide="check"></i>' : ''}
        </div>
        <div class="library-song-card-play">
          <i data-lucide="play"></i>
        </div>
      </div>
      <div class="library-song-card-info">
        <div class="library-song-card-title" title="${escapeAttr(song.title)}">${escapeHtml(song.title)}</div>
        <div class="library-song-card-meta">
          <span class="library-song-card-duration">
            <i data-lucide="clock"></i>
            ${formatDuration(song.duration)}
          </span>
        </div>
      </div>
    </div>
  `).join('');

  // Handle broken images — replace with plasma placeholder on 403/error
  elements.libraryGrid.querySelectorAll('.library-song-card-image img').forEach(img => {
    const handler = () => {
      img.removeEventListener('error', handler);
      const songId = img.closest('.library-song-card')?.dataset.songId;
      const song = libraryBrowserState.songs.find(s => s.id === songId);
      console.warn(`[Library] Image 403: "${song?.title || 'unknown'}" (${songId}) — ${img.src}`);
      img.src = getPlaceholderForSong(songId);
    };
    img.addEventListener('error', handler);
  });

  // Add click handlers
  elements.libraryGrid.querySelectorAll('.library-song-card').forEach(card => {
    card.addEventListener('click', (e) => {
      e.stopPropagation(); // Prevent panel from closing
      // Check if the play button was clicked
      if (e.target.closest('.library-song-card-play')) {
        previewSong(card.dataset.songId);
        return;
      }
      handleSongClick(card.dataset.songId, e);
    });
  });

  // Re-initialize Lucide icons for the new elements
  if (window.lucide) {
    window.lucide.createIcons();
  }
}

/**
 * Render list view
 */
function renderListView(songs) {
  if (!elements.libraryList) return;

  elements.libraryList.innerHTML = songs.map(song => `
    <div class="library-song-row ${libraryBrowserState.selectedIds.has(song.id) ? 'selected' : ''}"
         data-song-id="${song.id}">
      <div class="library-song-row-checkbox">
        ${libraryBrowserState.selectedIds.has(song.id) ? '<i data-lucide="check"></i>' : ''}
      </div>
      <div class="library-song-row-image">
        ${song.imageUrl
          ? `<img src="${escapeAttr(song.imageUrl)}" alt="${escapeAttr(song.title)}" loading="lazy">`
          : `<img src="${getPlaceholderForSong(song.id)}" alt="" loading="lazy">`
        }
      </div>
      <div class="library-song-row-info">
        <div class="library-song-row-title">${escapeHtml(song.title)}</div>
        <div class="library-song-row-tags">${escapeHtml(song.tags || song.prompt || '')}</div>
      </div>
      <div class="library-song-row-duration">${formatDuration(song.duration)}</div>
      <div class="library-song-row-actions">
        <button class="library-song-row-action" data-action="add" title="Add to playlist">
          <i data-lucide="plus"></i>
        </button>
        <button class="library-song-row-action" data-action="play" title="Preview">
          <i data-lucide="play"></i>
        </button>
      </div>
    </div>
  `).join('');

  // Handle broken images — replace with plasma placeholder on 403/error
  elements.libraryList.querySelectorAll('.library-song-row-image img').forEach(img => {
    const handler = () => {
      img.removeEventListener('error', handler);
      const songId = img.closest('.library-song-row')?.dataset.songId;
      const song = libraryBrowserState.songs.find(s => s.id === songId);
      console.warn(`[Library] Image 403: "${song?.title || 'unknown'}" (${songId}) — ${img.src}`);
      img.src = getPlaceholderForSong(songId);
    };
    img.addEventListener('error', handler);
  });

  // Add click handlers
  elements.libraryList.querySelectorAll('.library-song-row').forEach(row => {
    row.addEventListener('click', (e) => {
      e.stopPropagation(); // Prevent panel from closing
      // Check for action button clicks
      const actionBtn = e.target.closest('.library-song-row-action');
      if (actionBtn) {
        const action = actionBtn.dataset.action;
        const songId = row.dataset.songId;
        if (action === 'add') {
          addSongToPlaylist(songId);
        } else if (action === 'play') {
          previewSong(songId);
        }
        return;
      }
      handleSongClick(row.dataset.songId, e);
    });
  });

  // Re-initialize Lucide icons for the new elements
  if (window.lucide) {
    window.lucide.createIcons();
  }
}

/**
 * Handle song click
 */
function handleSongClick(songId, event) {
  // Toggle selection
  if (libraryBrowserState.selectedIds.has(songId)) {
    libraryBrowserState.selectedIds.delete(songId);
  } else {
    libraryBrowserState.selectedIds.add(songId);
  }

  // Update selection UI
  updateSelectionUI();

  // Re-render to update checkbox states
  renderSongs();
}

/**
 * Update selection toolbar UI
 */
function updateSelectionUI() {
  const count = libraryBrowserState.selectedIds.size;
  const hasSelection = count > 0;

  // Show/hide selection toolbar
  if (elements.librarySelectionToolbar) {
    elements.librarySelectionToolbar.style.display = hasSelection ? 'flex' : 'none';
  }

  // Update count
  if (elements.librarySelectionCount) {
    elements.librarySelectionCount.textContent = count;
  }

  // Add selection-mode class to content for checkbox visibility
  if (elements.libraryContent) {
    elements.libraryContent.classList.toggle('selection-mode', hasSelection);
  }

  updateAddAllFilteredVisibility();
}

/**
 * Check if any narrowing (search/filter) is active for bulk operations.
 */
function hasActiveBulkScope() {
  const hasSearch = !!(libraryBrowserState.searchQuery && libraryBrowserState.searchQuery.trim());
  const hasBasicFilter = libraryBrowserState.filter && libraryBrowserState.filter !== 'all';
  return hasActiveFilters() || hasSearch || hasBasicFilter;
}

/**
 * Check if whole-library checkbox is enabled by user
 */
function isWholeLibrarySelected() {
  return !!elements.libraryWholeLibraryCheckbox?.checked;
}

/**
 * Update main bulk-action button label/function cue based on current state
 */
function updatePrimaryActionButton() {
  if (!elements.libraryAddAllFilteredBtn) return;

  const selectionCount = libraryBrowserState.selectedIds.size;
  const hasSelection = selectionCount > 0;
  const hasScopedBulk = hasActiveBulkScope();
  const wholeLibrary = isWholeLibrarySelected();
  const label = hasSelection
    ? `Add (${selectionCount}) Selected to Playlist`
    : (wholeLibrary && !hasScopedBulk ? 'Add All to Playlist' : 'Add All Filtered to Playlist');
  const icon = hasSelection ? 'plus' : 'list-plus';

  if (elements.libraryPrimaryActionText) {
    elements.libraryPrimaryActionText.textContent = label;
  }
  if (elements.libraryPrimaryActionIcon) {
    elements.libraryPrimaryActionIcon.setAttribute('data-lucide', icon);
  }
  elements.libraryAddAllFilteredBtn.title = label;

  if (window.lucide) {
    window.lucide.createIcons();
  }
}

/**
 * Handle click on primary bulk action button
 */
function handlePrimaryBulkAction() {
  if (libraryBrowserState.selectedIds.size > 0) {
    addSelectedToPlaylist();
    return;
  }
  if (!hasActiveBulkScope() && !isWholeLibrarySelected()) {
    return;
  }
  addAllFilteredToPlaylist();
}

/**
 * Clear selection
 */
function clearSelection() {
  libraryBrowserState.selectedIds.clear();
  updateSelectionUI();
  renderSongs();
}

/**
 * Select all visible (filtered) songs.
 * When client-side filters are active, only songs passing the filters are selected.
 */
function selectAll() {
  const visible = applyAdvancedFilters(libraryBrowserState.songs);
  visible.forEach(song => {
    libraryBrowserState.selectedIds.add(song.id);
  });
  updateSelectionUI();
  renderSongs();
}

/**
 * Add selected songs to playlist
 */
function addSelectedToPlaylist() {
  const selectedSongs = libraryBrowserState.songs.filter(
    song => libraryBrowserState.selectedIds.has(song.id)
  );

  if (selectedSongs.length === 0) return;

  // Convert to playlist format
  for (const song of selectedSongs) {
    addSongToPlaylistFromLibrary(song);
  }

  // Show playlist UI (transport bar, etc.) and update it
  showPlaylist();
  updatePlaylistUI();

  // Clear selection after adding
  clearSelection();

  // Show success notification
  showNotification(`Added ${selectedSongs.length} song${selectedSongs.length > 1 ? 's' : ''} to playlist`);
}

/**
 * Update visibility of the "Add All Filtered" button
 * Shows when filters or search are active
 */
function updateAddAllFilteredVisibility() {
  if (!elements.libraryAddAllFilteredBtn) return;
  elements.libraryAddAllFilteredBtn.style.display = 'inline-flex';
  updatePrimaryActionButton();
  updateWholeLibraryOptionVisibility();
  updateBulkActionAvailability();
}

/**
 * Show whole-library checkbox only when there is no scope (search/filter) and no selection.
 */
function updateWholeLibraryOptionVisibility() {
  const hasSelection = libraryBrowserState.selectedIds.size > 0;
  const hasScopedBulk = hasActiveBulkScope();
  const shouldShow = !hasScopedBulk && !hasSelection;

  if (elements.libraryWholeLibraryWrap) {
    elements.libraryWholeLibraryWrap.style.display = shouldShow ? 'block' : 'none';
    elements.libraryWholeLibraryWrap.classList.toggle('checked', isWholeLibrarySelected());
  }

  // Clear checked state when user applies any scope or uses explicit selection
  if (!shouldShow && elements.libraryWholeLibraryCheckbox?.checked) {
    elements.libraryWholeLibraryCheckbox.checked = false;
    if (elements.libraryWholeLibraryWrap) {
      elements.libraryWholeLibraryWrap.classList.remove('checked');
    }
  }
}

/**
 * Enable/disable primary action and export options based on selection/scope/whole-library intent.
 */
function updateBulkActionAvailability() {
  const hasSelection = libraryBrowserState.selectedIds.size > 0;
  const hasScopedBulk = hasActiveBulkScope();
  const wholeLibrary = isWholeLibrarySelected();
  const canRunBulkActions = hasSelection || hasScopedBulk || wholeLibrary;

  if (elements.libraryAddAllFilteredBtn) {
    elements.libraryAddAllFilteredBtn.disabled = !canRunBulkActions;
    elements.libraryAddAllFilteredBtn.classList.toggle('disabled', !canRunBulkActions);
  }

  // Scope to export menu (not #libraryPanel) for reparenting support
  const exportContainer = elements.libraryExportMenu || document;
  exportContainer.querySelectorAll('.library-export-option').forEach(btn => {
    btn.disabled = !canRunBulkActions;
    btn.classList.toggle('disabled', !canRunBulkActions);
  });
}

// Timer for auto-hiding the add-all progress message
let addAllProgressTimer = null;

/**
 * Show the inline progress bar above the Add All Filtered button.
 */
function showAddAllProgress(text) {
  if (!elements.libraryAddAllProgress) return;
  clearTimeout(addAllProgressTimer);
  elements.libraryAddAllProgress.classList.remove('success', 'error');
  elements.libraryAddAllProgress.style.display = '';
  if (elements.libraryAddAllProgressText) {
    elements.libraryAddAllProgressText.textContent = text;
  }
}

/**
 * Update just the progress text.
 */
function updateAddAllProgress(text) {
  if (elements.libraryAddAllProgressText) {
    elements.libraryAddAllProgressText.textContent = text;
  }
}

/**
 * Show a completion message (hides the progress track), then auto-hide after delay.
 */
function showAddAllComplete(text, variant = 'success') {
  if (!elements.libraryAddAllProgress) return;
  clearTimeout(addAllProgressTimer);
  elements.libraryAddAllProgress.classList.remove('success', 'error');
  elements.libraryAddAllProgress.classList.add(variant);
  if (elements.libraryAddAllProgressText) {
    elements.libraryAddAllProgressText.textContent = text;
  }
  addAllProgressTimer = setTimeout(() => hideAddAllProgress(), 3000);
}

/**
 * Hide the progress container and re-show the button.
 */
function hideAddAllProgress() {
  clearTimeout(addAllProgressTimer);
  if (elements.libraryAddAllProgress) {
    elements.libraryAddAllProgress.style.display = 'none';
    elements.libraryAddAllProgress.classList.remove('success', 'error');
  }
  updateAddAllFilteredVisibility();
}

/**
 * Add all songs matching current filters/search to the playlist.
 * Starts from the client-side cache (already-loaded songs), then fetches remaining
 * pages using cursor loop. Applies client-side filters, deduplicates.
 */
async function addAllFilteredToPlaylist() {
  const MAX_PAGES = 50; // Safety limit for remaining pages
  const BULK_FETCH_PAGE_SIZE = 100; // Larger page size to reduce API calls
  const FETCH_LOOP_DELAY = 500; // ms between cursor-loop API calls

  const hasNarrowing = hasActiveBulkScope();
  const wholeLibrary = isWholeLibrarySelected();
  if (!hasNarrowing && !wholeLibrary) {
    showNotification('Select a filter/search or enable "Select my entire Suno library" first.', 'error');
    return;
  }

  // Disable button to prevent double-clicks
  if (elements.libraryAddAllFilteredBtn) {
    elements.libraryAddAllFilteredBtn.disabled = true;
  }

  // Guard: prevent autoFetchForFilters and loadMore from competing
  libraryBrowserState.isLoading = true;

  // Start from client cache — songs already loaded via scrolling
  let allSongs = [...libraryBrowserState.songs];
  let cursor = libraryBrowserState.nextCursor;
  let needsFetch = libraryBrowserState.hasMore && !!cursor;
  let pageCount = 0;

  // Snapshot filter state at start to avoid drift if user changes filters during fetch
  const filterSnapshot = JSON.parse(JSON.stringify(libraryBrowserState.advancedFilters));
  const searchSnapshot = libraryBrowserState.searchQuery;
  const basicFilterSnapshot = libraryBrowserState.filter;

  showAddAllProgress(needsFetch
    ? `Fetching songs... (${allSongs.length} cached)`
    : 'Adding songs...');

  try {
    // Fetch remaining pages (skipped entirely if all pages already loaded)
    const RETRY_DELAYS = [1000, 5000]; // 1s before 1st retry, 5s before 2nd
    while (needsFetch) {
      // Delay between API calls to avoid rate limits (skip first call)
      if (pageCount > 0) {
        await new Promise(r => setTimeout(r, FETCH_LOOP_DELAY));
      }

      const options = {
        cursor,
        pageSize: BULK_FETCH_PAGE_SIZE,
        filter: basicFilterSnapshot
      };

      if (searchSnapshot) {
        options.searchText = searchSnapshot;
      }
      let result;
      let lastError;
      for (let attempt = 0; attempt <= RETRY_DELAYS.length; attempt++) {
        try {
          if (attempt > 0) {
            updateAddAllProgress(`Retrying... (${allSongs.length} found so far)`);
            await new Promise(r => setTimeout(r, RETRY_DELAYS[attempt - 1]));
          }
          result = await sunoService.browseLibrary(options);
          break;
        } catch (err) {
          lastError = err;
          console.warn(`[LibraryBrowser] addAllFiltered: page fetch attempt ${attempt + 1} failed:`, err.message);
        }
      }
      if (!result) throw lastError;

      allSongs = allSongs.concat(result.songs);
      cursor = result.nextCursor;
      pageCount++;

      updateAddAllProgress(`Fetching songs... (${allSongs.length} found)`);

      // Stop if no more pages or hit safety limit
      if (!result.hasMore || !cursor || pageCount >= MAX_PAGES) {
        break;
      }
    }

    // Sync browser state with fetched data so infinite scroll stays consistent
    if (pageCount > 0) {
      libraryBrowserState.songs = allSongs;
      libraryBrowserState.nextCursor = cursor;
      libraryBrowserState.hasMore = !!cursor;
      renderSongsCore();
    }

    // Apply client-side advanced filters using snapshot (not current state)
    const filteredSongs = applyAdvancedFiltersWithSnapshot(allSongs, filterSnapshot);

    if (filteredSongs.length === 0) {
      showAddAllComplete('No songs match the current filters', 'error');
      return;
    }

    // Deduplicate against existing playlist by sunoClipId
    const existingClipIds = new Set(
      playlistState.playlist
        .filter(t => t.sunoClipId)
        .map(t => t.sunoClipId)
    );
    const newSongs = filteredSongs.filter(s => !existingClipIds.has(s.id));

    if (newSongs.length === 0) {
      showAddAllComplete('All songs already in playlist');
      return;
    }

    // Add songs to playlist
    for (const song of newSongs) {
      addSongToPlaylistFromLibrary(song);
    }

    showPlaylist();
    updatePlaylistUI();
    const label = newSongs.length === 1 ? '1 song' : `${newSongs.length} songs`;
    showAddAllComplete(`${label} added to playlist`);
  } catch (error) {
    console.error('[LibraryBrowser] addAllFilteredToPlaylist error:', error);
    // If we have songs from cache + partial fetch, continue with those
    if (allSongs.length > 0) {
      console.warn('[LibraryBrowser] addAllFiltered: fetch stopped early, continuing with', allSongs.length, 'songs:', error.message);
      // Sync browser state with whatever we managed to fetch
      if (allSongs.length > libraryBrowserState.songs.length) {
        libraryBrowserState.songs = allSongs;
        libraryBrowserState.nextCursor = cursor;
        libraryBrowserState.hasMore = !!cursor;
        renderSongsCore();
      }
      const filteredSongs = applyAdvancedFiltersWithSnapshot(allSongs, filterSnapshot);
      if (filteredSongs.length === 0) {
        showAddAllComplete('No matching songs found', 'error');
        return;
      }
      const existingClipIds = new Set(
        playlistState.playlist
          .filter(t => t.sunoClipId)
          .map(t => t.sunoClipId)
      );
      const newSongs = filteredSongs.filter(s => !existingClipIds.has(s.id));
      if (newSongs.length === 0) {
        showAddAllComplete('All songs already in playlist');
        return;
      }
      for (const song of newSongs) {
        addSongToPlaylistFromLibrary(song);
      }
      showPlaylist();
      updatePlaylistUI();
      const label = newSongs.length === 1 ? '1 song' : `${newSongs.length} songs`;
      showAddAllComplete(`${label} added (some songs couldn't be retrieved)`);
    } else {
      showAddAllComplete('Unable to retrieve songs. Please try again.', 'error');
    }
  } finally {
    libraryBrowserState.isLoading = false;
    if (elements.libraryAddAllFilteredBtn) {
      elements.libraryAddAllFilteredBtn.disabled = false;
    }
  }
}

/**
 * Add a single song to playlist
 */
function addSongToPlaylist(songId) {
  const song = libraryBrowserState.songs.find(s => s.id === songId);
  if (song) {
    addSongToPlaylistFromLibrary(song);
    showPlaylist();
    updatePlaylistUI();
    showNotification(`Added "${song.title}" to playlist`);
  }
}

/**
 * Convert library song to playlist format and add
 */
function addSongToPlaylistFromLibrary(song) {
  // Create a playlist item that matches the expected format in playback.js
  // playback.js checks for: track.isStreamed && track.streamUrl
  const playlistItem = {
    // Streamed track (URL-based, not a File object)
    isStreamed: true,
    streamUrl: song.audioUrl,
    name: song.title,
    included: true,
    duration: song.duration,
    audioBuffer: null,  // Will be loaded on play
    lyrics: null,
    lyricsFile: null,
    alignedLyrics: null,  // Time-synced lyrics from Suno API
    error: false,
    errorMessage: null,
    addedAt: Date.now(),
    // Additional metadata from Suno
    sunoClipId: song.id,
    imageUrl: song.imageUrl,
    // Suno-specific metadata for filtering
    sunoMeta: {
      tags: song.tags || '',           // Genre/style tags
      isLiked: song.isLiked || false,  // Liked status
      isInstrumental: !song.hasLyrics, // Instrumental vs vocal (true if no lyrics)
      createdAt: song.createdAt || null, // Creation date from Suno
      prompt: song.prompt || ''        // Original prompt/theme
    }
  };

  // Add to playlist state and get the index
  const index = playlistState.playlist.length;
  playlistState.playlist.push(playlistItem);

  // Suno songs arrive with durations from API metadata — mark as loaded
  // so duration-dependent features (total duration, clean up) work
  markDurationsLoadedIfReady();

  // Fetch aligned lyrics asynchronously (non-blocking)
  fetchAlignedLyricsForTrack(index, song.id);
}

/**
 * Fetch aligned lyrics for a track asynchronously
 * Updates the playlist item when lyrics are available
 */
async function fetchAlignedLyricsForTrack(index, clipId) {
  try {
    const alignedLyrics = await sunoService.getAlignedLyrics(clipId);

    // Verify track still exists at index with same clipId (playlist may have changed)
    const track = playlistState.playlist[index];
    if (!track || track.sunoClipId !== clipId) {
      console.log('[LibraryBrowser] Track moved or removed, skipping lyrics update');
      return;
    }

    if (alignedLyrics) {
      track.alignedLyrics = alignedLyrics;
      // Also set plain text lyrics for display/fallback
      track.lyrics = alignedLyrics.lines.map(l => l.text).join('\n');
      updatePlaylistUI();

      // If this is the currently playing track, recalculate scroll params
      if (playlistState.currentTrackIndex === index) {
        // Dynamically import to avoid circular dependency
        const { recalculateLyricsScrollParams } = await import('./visualizer-controls.js');
        recalculateLyricsScrollParams();
      }

      console.log(`[LibraryBrowser] Loaded aligned lyrics for track: ${track.name}`);
    }
  } catch (error) {
    console.warn('[LibraryBrowser] Failed to fetch aligned lyrics:', error);
  }
}

/**
 * Preview song (play audio URL)
 * Adds the song to the playlist if not already present, then loads and plays it.
 *
 * IMPORTANT: This function implements the preview play button functionality.
 * The play buttons appear in both grid view (.library-song-card-play) and
 * list view (data-action="play"). Both should call this function.
 *
 * If this function is modified, ensure both grid and list view click handlers
 * continue to work correctly. See:
 * - renderGridView(): handles .library-song-card-play clicks
 * - renderListView(): handles data-action="play" clicks
 */
async function previewSong(songId) {
  const song = libraryBrowserState.songs.find(s => s.id === songId);
  if (!song?.audioUrl) {
    showNotification('Unable to preview: no audio URL available', 'error');
    return;
  }

  // Check if song is already in playlist by its Suno clip ID
  let trackIndex = playlistState.playlist.findIndex(t => t.sunoClipId === songId);

  if (trackIndex === -1) {
    // Song not in playlist - add it
    addSongToPlaylistFromLibrary(song);
    trackIndex = playlistState.playlist.length - 1;
    showPlaylist();
    updatePlaylistUI();
    showNotification(`Added "${song.title}" to playlist`);
  }

  // Load and play the track
  try {
    const loaded = await loadTrack(trackIndex);
    if (loaded) {
      await startPlayback(0);
    }
  } catch (error) {
    console.error('[LibraryBrowser] Preview error:', error);
    showNotification('Error playing track: ' + error.message, 'error');
  }
}

/**
 * Toggle export menu
 */
function toggleExportMenu() {
  if (elements.libraryExportMenu) {
    const isVisible = elements.libraryExportMenu.style.display !== 'none';
    elements.libraryExportMenu.style.display = isVisible ? 'none' : 'block';
  }
}

/**
 * Hide export menu
 */
function hideExportMenu() {
  if (elements.libraryExportMenu) {
    elements.libraryExportMenu.style.display = 'none';
  }
}

/**
 * Export selected songs
 */
async function exportSelected(format) {
  const selectedSongs = libraryBrowserState.songs.filter(
    song => libraryBrowserState.selectedIds.has(song.id)
  );

  if (selectedSongs.length === 0) return;

  libraryBrowserState.isExporting = true;
  showExportProgress();

  // Set up progress listener
  exportProgressCleanup = sunoService.onExportProgress((current, total, song) => {
    updateExportProgress(current, total);
  });

  try {
    const songs = selectedSongs.map(s => ({
      clipId: s.id,
      title: s.title
    }));

    const result = await sunoService.exportBatch(songs, format);

    hideExportProgress();

    if (result.success) {
      showNotification(`Exported ${result.completed} song${result.completed > 1 ? 's' : ''}`);
    } else if (result.completed > 0) {
      showNotification(`Exported ${result.completed}/${songs.length} songs (${result.failed} failed)`);
    } else {
      showNotification('Export failed: ' + (result.errors[0] || 'Unknown error'), 'error');
    }
  } catch (error) {
    console.error('[LibraryBrowser] Export error:', error);
    showNotification('Export failed: ' + error.message, 'error');
    hideExportProgress();
  } finally {
    libraryBrowserState.isExporting = false;
    if (exportProgressCleanup) {
      exportProgressCleanup();
      exportProgressCleanup = null;
    }
  }
}

/**
 * Cancel export
 */
function cancelExport() {
  // Note: We can't actually cancel ongoing downloads, but we can stop progress updates
  libraryBrowserState.isExporting = false;
  hideExportProgress();
  if (exportProgressCleanup) {
    exportProgressCleanup();
    exportProgressCleanup = null;
  }
}

/**
 * Show export progress
 */
function showExportProgress() {
  if (elements.libraryExportProgress) {
    elements.libraryExportProgress.style.display = 'flex';
  }
}

/**
 * Hide export progress
 */
function hideExportProgress() {
  if (elements.libraryExportProgress) {
    elements.libraryExportProgress.style.display = 'none';
  }
  libraryBrowserState.exportProgress = { current: 0, total: 0 };
}

/**
 * Update export progress UI
 */
function updateExportProgress(current, total) {
  libraryBrowserState.exportProgress = { current, total };

  if (elements.libraryExportProgressText) {
    elements.libraryExportProgressText.textContent = `${current} / ${total}`;
  }

  if (elements.libraryExportProgressBar) {
    const percent = total > 0 ? (current / total) * 100 : 0;
    elements.libraryExportProgressBar.style.width = `${percent}%`;
  }
}

/**
 * Show loading state
 */
function showLoading() {
  if (elements.libraryLoading) {
    elements.libraryLoading.style.display = 'flex';
  }
  if (elements.libraryGrid) {
    elements.libraryGrid.innerHTML = '';
  }
  if (elements.libraryList) {
    elements.libraryList.innerHTML = '';
  }
  hideEmpty();
  hideError();
}

/**
 * Hide loading state
 */
function hideLoading() {
  if (elements.libraryLoading) {
    elements.libraryLoading.style.display = 'none';
  }
}

/**
 * Show empty state
 */
function showEmpty() {
  hideLoading();
  if (elements.libraryEmpty) {
    // During auto-fetch, keep empty state hidden — the indicator is shown
    // in the load-more area instead, and songs may appear as pages arrive.
    elements.libraryEmpty.style.display = isAutoFetching ? 'none' : 'flex';
  }
  if (elements.libraryEmptyMessage) {
    if (isAutoFetching) {
      elements.libraryEmptyMessage.textContent = 'Searching your library for matching songs...';
    } else if (libraryBrowserState.searchQuery) {
      elements.libraryEmptyMessage.textContent = `No songs found for "${libraryBrowserState.searchQuery}"`;
    } else if (hasActiveFilters()) {
      elements.libraryEmptyMessage.textContent = 'No songs match your filters.';
    } else if (libraryBrowserState.filter === 'liked') {
      elements.libraryEmptyMessage.textContent = "You haven't liked any songs yet.";
    } else {
      elements.libraryEmptyMessage.textContent = 'Your Suno library is empty.';
    }
  }
  if (elements.libraryLoadMore) {
    // Keep load-more visible during auto-fetch (shows the spinner indicator)
    elements.libraryLoadMore.style.display = isAutoFetching ? 'flex' : 'none';
  }
}

/**
 * Hide empty state
 */
function hideEmpty() {
  if (elements.libraryEmpty) {
    elements.libraryEmpty.style.display = 'none';
  }
}

/**
 * Show error state
 */
function showError(message) {
  hideLoading();
  if (elements.libraryError) {
    elements.libraryError.style.display = 'flex';
  }
  if (elements.libraryErrorMessage) {
    elements.libraryErrorMessage.textContent = message || 'Please check your connection and try again.';
  }
}

/**
 * Hide error state
 */
function hideError() {
  if (elements.libraryError) {
    elements.libraryError.style.display = 'none';
  }
}

/**
 * Show notification (toast)
 */
function showNotification(message, type = 'success') {
  // Create or reuse notification element
  let notification = document.getElementById('libraryNotification');
  if (!notification) {
    notification = document.createElement('div');
    notification.id = 'libraryNotification';
    notification.className = 'library-notification';
    document.body.appendChild(notification);
  }

  notification.textContent = message;
  notification.className = `library-notification library-notification-${type} show`;

  // Auto-hide after 3 seconds
  setTimeout(() => {
    notification.classList.remove('show');
  }, 3000);
}

/**
 * Format duration in seconds to MM:SS
 */
function formatDuration(seconds) {
  if (!seconds || isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Reset library browser state (for testing or when extension becomes unavailable)
 */
export function resetLibraryBrowser() {
  // Clean up IntersectionObserver
  destroyInfiniteScroll();

  libraryBrowserState.songs = [];
  libraryBrowserState.hasMore = false;
  libraryBrowserState.total = 0;
  libraryBrowserState.nextCursor = null;
  libraryBrowserState.searchQuery = '';
  libraryBrowserState.selectedIds.clear();
  libraryBrowserState.isLoading = false;
  libraryBrowserState.error = null;

  // Reset advanced filters (Issue #7, #13)
  libraryBrowserState.advancedFilters = {
    genre: '',
    dateFrom: null,
    dateTo: null,
    audioType: 'all',
    theme: '',
    likedStatus: 'all',
    generationOrder: 'all',
    minLength: null
  };
  libraryBrowserState.filterPanelOpen = false;
  libraryBrowserState.filter = 'all';

  // Reset filter inputs
  if (elements.libraryFilterGenre) elements.libraryFilterGenre.value = '';
  if (elements.libraryFilterTheme) elements.libraryFilterTheme.value = '';
  if (elements.libraryFilterDateFrom) elements.libraryFilterDateFrom.value = '';
  if (elements.libraryFilterLength) elements.libraryFilterLength.value = '';
  if (elements.libraryFilterDateTo) elements.libraryFilterDateTo.value = '';

  // Reset filter panel and toggle
  if (elements.libraryFilterPanel) elements.libraryFilterPanel.style.display = 'none';
  if (elements.libraryFilterToggle) {
    elements.libraryFilterToggle.classList.remove('active', 'has-filters');
  }
  if (elements.libraryWholeLibraryCheckbox) {
    elements.libraryWholeLibraryCheckbox.checked = false;
  }
  if (elements.libraryWholeLibraryWrap) {
    elements.libraryWholeLibraryWrap.style.display = 'none';
    elements.libraryWholeLibraryWrap.classList.remove('checked');
  }
  hideAddAllProgress();

  // Reset filter toggle groups (scoped to filter panel for reparenting support)
  if (elements.libraryFilterPanel) {
    elements.libraryFilterPanel.querySelectorAll('.library-filter-toggle-group').forEach(group => {
      group.querySelectorAll('.library-filter-option').forEach(btn => {
        btn.classList.toggle('active', btn.dataset.value === 'all');
      });
    });
  }

  // Clear filter badges
  updateFilterBadges();

  if (elements.libraryGrid) {
    elements.libraryGrid.innerHTML = '';
  }
  if (elements.libraryList) {
    elements.libraryList.innerHTML = '';
  }

  hideLoading();
  hideEmpty();
  hideError();
  updateSelectionUI();
  updateAddAllFilteredVisibility();
}
